import { CollectionPaginationTriggersProp, CmsFilter, CmsFilterOperator } from 'types/cms';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useListCollectionItems } from 'client/queries/cms/list-collection-items';
import { isVectorSearch } from 'common/utils/cms/vector-search';
import { usePaginationTriggers } from './use-pagination-triggers';
import { FilterReducerAction, useFilterReducer, useFilterTriggers } from './use-filter-triggers';

type UseDataParams = {
	collectionId: string;
	pageSize: number;
	paginationTriggers?: CollectionPaginationTriggersProp;
	collectionFilters?: CmsFilter[];
};

export const useData = ({ collectionId, pageSize, paginationTriggers, collectionFilters = [] }: UseDataParams) => {
	const [filters, _setFilters] = useFilterReducer(collectionFilters);
	const [page, setPage] = useState(0);
	const setFilters = useCallback(
		(newFilters: FilterReducerAction) => {
			_setFilters(newFilters);
			setPage(0);
		},
		[_setFilters]
	);

	useFilterTriggers({ filters: collectionFilters, setFilters });

	/**
	 * note
	 * - current implementation supports only one filter condition
	 * - it combines search and filter conditions with AND operator, also includes pagination
	 */
	const filterConditions = useMemo(() => {
		const { filter, search } = filters;
		const searchOperator = search?.operator || CmsFilterOperator.Contains;
		return [
			search &&
				(isVectorSearch(search)
					? { type: 'FIND' as const, field: search.field, value: search.value }
					: { type: 'FIND' as const, field: search.field, value: search.value, operator: searchOperator }),
			filter && { field: filter.field, value: filter.value, operator: filter.operator },
		].filter(Boolean) as NonNullable<Parameters<typeof useListCollectionItems>[0]['filter']>[];
	}, [filters]);

	const vectorSearch = isVectorSearch(filters.search ?? {}) ? filters.search : null;

	const items = useListCollectionItems({
		collectionId,
		pageSize,
		page,
		orderBy: 'position',
		orderDir: 'asc',
		...(filterConditions.length ? { filter: { operator: 'AND', conditions: filterConditions } } : null),
		...(vectorSearch
			? { vectorQuery: vectorSearch.value, vectorMinimumScore: vectorSearch.vectorMinimumScore }
			: null),
	});
	const { hasNextPage } = items;

	const fetchNextPage = useCallback(() => {
		if (hasNextPage) {
			setPage(currentPage => currentPage + 1);
		}
	}, [hasNextPage]);

	const fetchPreviousPage = useCallback(() => {
		setPage(currentPage => Math.max(currentPage - 1, 0));
	}, []);

	usePaginationTriggers({
		triggers: paginationTriggers,
		fetchNextPage,
		fetchPreviousPage,
		hasNextPage: items.hasNextPage,
		hasPreviousPage: items.hasPreviousPage,
	});

	const pageCurrent = page;
	const [currentItems, setCurrentItems] = useState(items.data?.pages.findLast(p => p.page === pageCurrent));

	useEffect(() => {
		if (items.data && items.isSuccess && !items.isFetching) {
			setCurrentItems(items.data.pages.findLast(p => p.page === pageCurrent));
		}
	}, [items.data, items.isSuccess, items.isFetching, pageCurrent]);

	const itemsMax = currentItems?.total ?? 0;
	const pageMax = Math.max(0, Math.ceil(itemsMax / pageSize) - 1);

	return {
		collectionId,
		items: currentItems,
		isLoading: items.isLoading,
		hasNext: items.hasNextPage,
		hasPrev: items.hasPreviousPage,
		pageCurrent,
		pageMax,
		itemsMax,
	};
};
